import ContentAddCircle from 'material-ui/svg-icons/content/add-circle';
import { func, object, string } from 'prop-types';
import { path, fromPairs } from 'ramda';
import React from 'react';

import { colors } from 'styles/provider';
import ContentColumns from '../../components/ContentColumns';
import ControlSteps from '../../components/ControlSteps';
import TextAreaWithSuggestions from '../../components/TextAreaWithSuggestions';
import NeurologistText from './NeurologistText';

const addCircleStyle = {
  width: '3rem',
  height: '3rem',
  color: colors.clrSecondary,
  display: 'block',
};
const iconAreaStyle = {
  padding: '0 0.5rem 0 0',
};

// Placeholders, change these names to something more descriptive when they are defined somewhere
const suggestions = [
  'mean1cog',
  'mean2cog',
  'mean3cog',
  'mean4cog',
  'mean5cog',
  'mean6cog',
  'mean7cog',
  'mean8cog',
  'mean9cog',
  'mean10cog',
  'mean11cog',
];
const suggestionsLocale = 'onlineRehabilitation.opts.';

const View3 = ({ neurologistsComments }, { fm, fmString, formValues, onChange }) => {
  const followedPlan = path(['rehab6', 'followedPlan'], formValues);

  React.useEffect(() => {
    let changes = {};

    // On mount check if there are planned changes in rehab5, and copy them to this rehab session
    const previousChanges = path(['rehab5', 'plannedChangesToManageCognitiveProblems'], formValues);
    const currentChanges = path(['rehab6', 'plannedChangesToManageCognitiveProblems'], formValues);
    if (!currentChanges && previousChanges) {
      changes = { ...changes, plannedChangesToManageCognitiveProblems: previousChanges };
    }
    // On mount get "goal" steps contents from rehab5
    const newSteps = [];
    ['currentStatus', 'clearlyWorseStatus', 'nearFutureTarget', 'betterStatus', 'clearlyBetterStatus'].forEach(step => {
      const previousStep = path(['rehab5', step], formValues);
      const currentStep = path(['rehab6', step], formValues);

      if (!currentStep && previousStep) {
        newSteps.push([step, previousStep]);
      }
    });
    if (newSteps.length > 0) {
      const newStepsObj = fromPairs(newSteps);
      changes = { ...changes, ...newStepsObj };
    }

    if (Object.keys(changes).length > 0) {
      onChange(changes);
    }
  }, []);

  const controlStepData = {
    currentStatus: path(['rehab6', 'currentStatus'], formValues),
    clearlyWorseStatus: path(['rehab6', 'clearlyWorseStatus'], formValues),
    nearFutureTarget: path(['rehab6', 'nearFutureTarget'], formValues),
    betterStatus: path(['rehab6', 'betterStatus'], formValues),
    clearlyBetterStatus: path(['rehab6', 'clearlyBetterStatus'], formValues),
  };

  const getValue = (name, index) => {
    return path(['rehab6', name, index], formValues) || '';
  };

  const onChangePlans = (name, index) => event => {
    const allValues = path(['rehab6', name], formValues) || ['', '', ''];
    const eventValue = path(['currentTarget', 'value'], event);
    // Copy array so that changes dont roll backwards
    const allValuesMapped = allValues.map(a => a);
    allValuesMapped[index] = eventValue;
    onChange({ [name]: allValuesMapped });
  };

  const addNewPlan = () => {
    const allValues = path(['rehab6', 'plannedChangesToManageCognitiveProblems'], formValues);
    const newIndex = Array.isArray(allValues) ? allValues.length : 3;
    onChangePlans('plannedChangesToManageCognitiveProblems', newIndex)({ currentTarget: { value: '' } });
  };

  const onChangeStep = name => event => {
    const eventValue = path(['currentTarget', 'value'], event);
    onChange({ [name]: eventValue });
  };

  const plans = path(['rehab6', 'plannedChangesToManageCognitiveProblems'], formValues) || [];
  return (
    <React.Fragment>
      <ContentColumns
        firstColumn={
          <div>
            <h2>{fm(`onlineRehabilitation.rehab6.3.header`)}</h2>
            <br />
            <div>{fm(`onlineRehabilitation.rehab6.3.${!followedPlan ? 'textNegative' : 'textPositive'}`)}</div>
            <br />

            <div>
              <ControlSteps steps={controlStepData} editable onEdit={onChangeStep} cognition fm={fm} />
              <br />
              <div>{fm(`onlineRehabilitation.rehab6.3.${!followedPlan ? 'text2Negative' : 'text2Positive'}`)}</div>

              <TextAreaWithSuggestions
                header={fmString('onlineRehabilitation.rehab5.8.changeHeader', { n: 1 })}
                onChange={onChangePlans('plannedChangesToManageCognitiveProblems', 0)}
                value={getValue('plannedChangesToManageCognitiveProblems', 0)}
                name={'plannedChangesToManageCognitiveProblems'}
                textPlaceholder={fmString('onlineRehabilitation.rehab5.8.changePlaceholder', { n: 1 })}
                suggestions={suggestions}
                suggestionsLocale={suggestionsLocale}
              />
              <TextAreaWithSuggestions
                header={fmString('onlineRehabilitation.rehab5.8.changeHeader', { n: 2 })}
                onChange={onChangePlans('plannedChangesToManageCognitiveProblems', 1)}
                value={getValue('plannedChangesToManageCognitiveProblems', 1)}
                name={'plannedChangesToManageCognitiveProblems'}
                textPlaceholder={fmString('onlineRehabilitation.rehab5.8.changePlaceholder', { n: 2 })}
                suggestions={suggestions}
                suggestionsLocale={suggestionsLocale}
              />
              <TextAreaWithSuggestions
                header={fmString('onlineRehabilitation.rehab5.8.changeHeader', { n: 3 })}
                onChange={onChangePlans('plannedChangesToManageCognitiveProblems', 2)}
                value={getValue('plannedChangesToManageCognitiveProblems', 2)}
                name={'plannedChangesToManageCognitiveProblems'}
                textPlaceholder={fmString('onlineRehabilitation.rehab5.8.changePlaceholder', { n: 3 })}
                suggestions={suggestions}
                suggestionsLocale={suggestionsLocale}
              />
              {plans.map((_, i) => {
                // Only map those added using the button
                if (i < 3) return null;
                return (
                  <TextAreaWithSuggestions
                    key={i}
                    header={fmString('onlineRehabilitation.rehab5.8.changeHeader', { n: i + 1 })}
                    onChange={onChangePlans('plannedChangesToManageCognitiveProblems', i)}
                    value={getValue('plannedChangesToManageCognitiveProblems', i)}
                    name={'plannedChangesToManageCognitiveProblems'}
                    textPlaceholder={fmString('onlineRehabilitation.rehab5.8.changePlaceholder', { n: i + 1 })}
                    suggestions={suggestions}
                    suggestionsLocale={suggestionsLocale}
                  />
                );
              })}
              <br />
              <button
                style={{
                  display: 'flex',
                  cursor: 'pointer',
                  alignItems: 'center',
                  border: 0,
                  backgroundColor: '#fff',
                }}
                onClick={addNewPlan}
              >
                <div style={iconAreaStyle}>
                  <ContentAddCircle style={addCircleStyle} />
                </div>
                <h3>{fm('onlineRehabilitation.addChange')}</h3>
              </button>
            </div>
            <br />
            <div>{fm('onlineRehabilitation.rehab6.3.text3')}</div>
          </div>
        }
        secondColumn={<NeurologistText text={neurologistsComments} />}
      />
    </React.Fragment>
  );
};

View3.contextTypes = {
  fm: func,
  fmString: func,
  formValues: object,
  onChange: func,
};
View3.propTypes = {
  neurologistsComments: string,
};
export default View3;
